import { BlockEditProps, registerBlockType } from "@wordpress/blocks";
import React, { useEffect } from "react";
import config, { type BlockAttributes } from "./block.json";
import { InnerBlocks, RichText } from "@wordpress/block-editor";
import { useBlockProps, useInnerBlocksProps } from "@wordpress/block-editor";
import { useSelect } from "@wordpress/data";
import { SVG, Path } from "@wordpress/components";

// Extend BlockAttributes to include index and other required attributes
interface ExtendedBlockAttributes extends BlockAttributes {
  index?: number;
  id?: string;
  title?: string;
}

export default function AccordionItemEditor(
  props: BlockEditProps<ExtendedBlockAttributes>
) {
  const { clientId, attributes, setAttributes } = props;

  // Generate an ID if one doesn't exist
  useEffect(() => {
    if (!attributes.id) {
      setAttributes({ id: `accordion-${clientId.slice(0, 8)}` });
    }
  }, [attributes.id, clientId, setAttributes]);

  // Get the block's index within its parent
  const blockIndex = useSelect(
    (select) => {
      const { getBlockIndex, getBlockRootClientId } = select(
        "core/block-editor"
      ) as {
        getBlockIndex: (clientId: string, rootClientId: string) => number;
        getBlockRootClientId: (clientId: string) => string;
      };
      const rootClientId = getBlockRootClientId(clientId);
      return getBlockIndex(clientId, rootClientId);
    },
    [clientId]
  );

  // Update the index attribute when block position changes
  useEffect(() => {
    if (blockIndex !== undefined && blockIndex !== attributes.index) {
      setAttributes({ index: blockIndex });
    }
  }, [blockIndex, attributes.index, setAttributes]);

  const blockProps = useBlockProps({
    className: "accordion-item",
  });

  const innerBlocksProps = useInnerBlocksProps(
    {
      className: "accordion-body",
    },
    {}
  );

  // Format the index to display as "01", "02", etc.
  const formattedIndex =
    blockIndex !== undefined
      ? `${(blockIndex + 1).toString().padStart(2, "0")}`
      : "";

  return (
    <div {...blockProps}>
      <h2 className="accordion-header">
        <div className="accordion-button collapsed">
          {/* Display the formatted index */}
          <span className="accordion-number me-5">{formattedIndex}</span>
          {/* Allow editing the title */}
          <RichText
            value={attributes.title ?? ""}
            tagName="p"
            className="copy-m"
            onChange={(title) => setAttributes({ title })}
            style={{
              backgroundColor: "transparent",
              fontFamily: "inherit",
              fontWeight: "inherit",
              fontSize: "inherit",
            }}
          />
        </div>
      </h2>
      <div className="accordion-collapse">
        <div {...innerBlocksProps} />
      </div>
    </div>
  );
}

registerBlockType<ExtendedBlockAttributes>(config.name, {
  ...config,

  attributes: {
    ...config.attributes,
    id: {
      type: "string",
      default: "",
    },
    index: {
      type: "number",
      default: 0,
    },
    title: {
      type: "string",
      source: "html",
      selector: "p.copy-m",
      default: "",
    },
  },

  icon() {
    return (
      <SVG viewBox="0 0 24 24">
        <Path d="M5 20.5a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2Zm-.5-2a.5.5 0 0 0 .5.5h14a.5.5 0 0 0 .5-.5v-7.563A2 2 0 0 1 19 11H5a2 2 0 0 1-.5-.063Zm.5-9h14a.5.5 0 0 0 .5-.5V5.5A.5.5 0 0 0 19 5H5a.5.5 0 0 0-.5.5V9a.5.5 0 0 0 .5.5ZM6 17v-1.5h6.5V17Zm0-3v-1.5h10.5V14Zm9.47-5.47-2-2 1.06-1.06L16 6.939l1.47-1.469 1.06 1.06-2 2a.751.751 0 0 1-1.061 0ZM6 8V6.5h6.5V8Z" />
      </SVG>
    );
  },

  edit: AccordionItemEditor,

  save({ attributes }) {
    const blockProps = useBlockProps.save({
      className: "accordion-item",
      id: attributes.id,
    });

    // Format the index for the saved output
    const formattedIndex =
      attributes.index !== undefined
        ? `${(attributes.index + 1).toString().padStart(2, "0")}`
        : "";

    return (
      <div {...blockProps}>
        <h2 className="accordion-header" id={`${attributes.id}-heading`}>
          <button
            className="accordion-button collapsed"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target={`#${attributes.id}-content`}
            aria-expanded="false"
            aria-controls={`${attributes.id}-content`}
          >
            <span className="accordion-number me-5">{formattedIndex}</span>
            <p className="copy-m">{attributes.title}</p>
            <div className="plus-container closed">
              <div className="wrapper">
                <div className="horizontal"></div>
                <div className="vertical"></div>
              </div>
            </div>
          </button>
        </h2>
        <div
          id={`${attributes.id}-content`}
          className="accordion-collapse collapse"
          aria-labelledby={`${attributes.id}-heading`}
          data-bs-parent=".accordion"
        >
          <div className="accordion-body">
            <InnerBlocks.Content />
          </div>
        </div>
      </div>
    );
  },
});
